<template>
  <div>
    <VisualEditorPill :class="{ hidden: showModal }">
      Hidden <span class="font-bold">{{ component }}</span> modal
    </VisualEditorPill>
    <div
      class="modal-wrapper fixed inset-0 z-1080"
      :class="showModal ? 'pointer-events-auto' : 'pointer-events-none'"
    >
      <div
        class="flex h-screen items-end justify-center pt-16 pb-4 text-center sm:block sm:p-0"
      >
        <!--
      Background overlay, show/hide based on modal state.

      Entering: "ease-out duration-300"
        From: "opacity-0"
        To: "opacity-100"
      Leaving: "ease-in duration-200"
        From: "opacity-100"
        To: "opacity-0"
    -->
        <!-- MODAL OVERLAY -->
        <div
          class="fixed inset-0 transition-opacity"
          aria-hidden="true"
          :class="[
            showModal
              ? 'opacity-100 duration-300 ease-out'
              : 'opacity-0 duration-200 ease-in',
          ]"
          @click="() => closeModal()"
        >
          <div class="absolute inset-0 bg-gray-500 opacity-75" />
        </div>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:h-screen sm:align-middle"
          aria-hidden="true"
          >&#8203;</span
        >
        <!--
      Modal panel, show/hide based on modal state.

      Entering: "ease-out duration-300"
        From: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        To: "opacity-100 translate-y-0 sm:scale-100"
      Leaving: "ease-in duration-200"
        From: "opacity-100 translate-y-0 sm:scale-100"
        To: "opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
    -->
        <FocusTrap
          :is-active="showModal"
          class="mx-3 my-auto inline-block max-h-full w-full rounded-lg px-6 pt-16 pb-6 text-left align-bottom shadow-xl transition-all sm:my-8 sm:mt-0 sm:h-auto sm:w-full sm:max-w-lg sm:align-middle lg:px-4 lg:pt-10 lg:pb-4"
          :class="[
            backgroundColorClass(blok.bgVariant),
            showModal
              ? 'translate-y-0 overflow-y-auto opacity-100 duration-300 ease-out sm:scale-100'
              : 'translate-y-4 overflow-hidden opacity-0 duration-200 ease-in sm:translate-y-0 sm:scale-95',
          ]"
          role="dialog"
          aria-modal="true"
        >
          <slot />
          <div
            v-if="!disableClose"
            class="absolute top-0 right-0 z-20 pt-4 pr-4"
          >
            <button
              type="button"
              class="rounded-md focus:outline-2 focus:outline-current"
              :class="[
                backgroundColorClass(blok.bgVariant),
                textColorClass(
                  blok.textVariant,
                  blok.bgVariant,
                  'text-lighter',
                ),
              ]"
              aria-label="Sluiten"
              @click="() => closeModal()"
            >
              <XMarkIcon class="h-8 w-8 lg:h-6 lg:w-6" />
            </button>
          </div>
        </FocusTrap>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeMount, onBeforeUnmount } from 'vue'
import ctaEventBus from '../ctaEventBus.js'
import VisualEditorPill from './VisualEditorPill.vue'
import FocusTrap from './FocusTrap.vue'
import { XMarkIcon } from '@heroicons/vue/24/solid'
import { useColorPalette } from '../composables/colorPalette'

const props = defineProps({
  showOnActionIntent: {
    type: String,
    required: true,
  },
  disableClose: {
    type: Boolean,
    default: false,
  },
  component: {
    type: String,
    required: true,
  },
  blok: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['modalClosed'])

const { backgroundColorClass, textColorClass } = useColorPalette()

const showModal = ref(false)

function closeModal(forceClose) {
  if (!props.disableClose || forceClose) {
    showModal.value = false
    enableScrollY()
    document.body.removeEventListener('keyup', handleKeydown)
    emit('modalClosed')
  }
}
function handleKeydown(event) {
  if (event.key === 'Escape') {
    closeModal()
  }
}
function disableScrollY() {
  document.body.style.top = `-${window.scrollY}px`
  document.body.style.left = 0
  document.body.style.right = 0
  document.body.style.position = 'fixed'
}
function enableScrollY() {
  const scrollY = document.body.style.top
  document.body.style.left = ''
  document.body.style.right = ''
  document.body.style.position = ''
  document.body.style.top = ''
  document.documentElement.style.scrollBehavior = 'auto' // prevent smooth scrolling for just this
  window.scrollTo(0, parseInt(scrollY || '0') * -1)
  document.documentElement.style.scrollBehavior = 'smooth'
}

onBeforeMount(() => {
  ctaEventBus.$on(props.showOnActionIntent, () => (showModal.value = true))
})
onBeforeUnmount(() => {
  closeModal(true)
  ctaEventBus.$off(props.showOnActionIntent)
})

watch(showModal, () => {
  if (showModal.value) {
    // handle scroll y position, read more: https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open/
    disableScrollY()
    document.body.addEventListener('keyup', handleKeydown)
  }
})
</script>

<style scoped>
@reference "../css/theme.css";

.editor-annotation:before {
  content: '';
  @apply absolute top-3 -left-4 h-0.5 w-4 bg-cyan-500;
}
</style>
